import React, { useState } from "react";
import axios from "axios";
import { useSite } from "../../context/SiteContext";
import { useToast } from "../../hooks/use-toast";
import { Mail, Phone, MapPin, ArrowUpRight, Linkedin, Youtube, Globe, CalendarDays, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { profile } = useSite();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", project: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "A few details missing",
        description: "Name, email and message are required."
      });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        project: form.project || null,
        message: form.message
      });
      setForm({ name: "", email: "", project: "", message: "" });
      toast({
        title: "Message sent",
        description: "Thanks — I'll get back to you shortly."
      });
    } catch (err) {
      let detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) detail = detail[0]?.msg || "Validation error";
      toast({
        title: "Couldn't send your message",
        description:
          typeof detail === "string"
            ? detail
            : "Please try again, or email me directly."
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#0a0a0a] py-24 md:py-36 border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Left — pitch */}
          <div className="md:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-300/90 mb-4">
              05 — Contact
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-stone-50 leading-[1.02]">
              Let's make something <span className="italic">worth watching.</span>
            </h2>
            <p className="mt-6 text-stone-400 text-base md:text-lg leading-[1.7] max-w-md">
              Currently open to Creative Director, Head of Content and Producer mandates — plus consulting on AI-native production pipelines.
            </p>

            {/* X1 + X3 — Calendly + CV download */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={profile.socials.calendly}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 bg-stone-50 text-zinc-950 px-5 py-3 uppercase tracking-[0.2em] text-[11px] font-medium hover:bg-amber-300 transition-colors"
              >
                <CalendarDays size={15} />
                Book a call
                <ArrowUpRight
                  size={14}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href={profile.cvUrl}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 border border-stone-300/70 text-stone-100 px-5 py-3 uppercase tracking-[0.2em] text-[11px] font-medium hover:bg-stone-100 hover:text-zinc-950 transition-colors"
              >
                <FileDown size={15} />
                Download CV
              </a>
            </div>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${profile.email}`}
                className="group flex items-center gap-4 text-stone-200 hover:text-amber-300 transition-colors"
              >
                <Mail size={18} className="text-amber-300" />
                <span className="font-display text-xl">{profile.email}</span>
                <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
              <a
                href={`tel:${profile.phone.replace(/\s/g, "")}`}
                className="group flex items-center gap-4 text-stone-200 hover:text-amber-300 transition-colors"
              >
                <Phone size={18} className="text-amber-300" />
                <span className="font-display text-xl">{profile.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-stone-400">
                <MapPin size={18} className="text-amber-300" />
                <span className="text-sm">
                  {profile.location} — available to travel
                </span>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3">
              <a
                href={profile.socials.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-stone-300 hover:border-amber-300 hover:text-amber-300 transition-colors"
              >
                <Linkedin size={16} />
              </a>
              <a
                href={profile.socials.youtube}
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-stone-300 hover:border-amber-300 hover:text-amber-300 transition-colors"
              >
                <Youtube size={16} />
              </a>
              <a
                href={profile.socials.website}
                target="_blank"
                rel="noreferrer"
                aria-label="Legacy portfolio"
                className="w-10 h-10 border border-zinc-800 flex items-center justify-center text-stone-300 hover:border-amber-300 hover:text-amber-300 transition-colors"
              >
                <Globe size={16} />
              </a>
            </div>
          </div>

          {/* Right — form */}
          <form
            onSubmit={submit}
            className="md:col-span-6 md:col-start-7 bg-[#0f0f0f] border border-zinc-800 p-8 md:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field
                label="Name"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Your full name"
              />
              <Field
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                placeholder="you@company.com"
              />
            </div>
            <div className="mt-6">
              <Field
                label="Project / Company"
                value={form.project}
                onChange={(v) => setForm({ ...form, project: v })}
                placeholder="(optional) — working title or brand"
              />
            </div>
            <div className="mt-6">
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">
                Brief
              </label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={5}
                placeholder="What are we making and when do you need it?"
                className="w-full bg-transparent border-b border-zinc-700 focus:border-amber-300 outline-none py-2 text-stone-100 placeholder:text-stone-600 transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="mt-8 group inline-flex items-center gap-3 bg-stone-50 text-zinc-950 px-6 py-3 uppercase tracking-[0.2em] text-[12px] font-medium hover:bg-amber-300 transition-colors disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send enquiry"}
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-transparent border-b border-zinc-700 focus:border-amber-300 outline-none py-2 text-stone-100 placeholder:text-stone-600 transition-colors"
    />
  </div>
);

export default Contact;
